import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { Router, ActivatedRoute } from "@angular/router";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import type { CalendarOptions, EventClickArg, DatesSetArg } from "@fullcalendar/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-events",
  templateUrl: "fui-events.component.html",
  standalone: false,
})
export class FuiEventsComponent extends BaseComponent implements OnInit {
  public events: any[] = [];
  public allEvents: any[] = [];
  public groupedEvents: { label: string; events: any[] }[] = [];
  public loading: boolean = true;
  public viewMode: string = "grid";
  public breadcrumbs: BreadcrumbItem[] = [];

  public calendarOptions: CalendarOptions = {
    initialView: "dayGridMonth",
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek",
    },
    events: [],
    eventClick: (info: EventClickArg) => this.onCalendarEventClick(info),
    datesSet: (info: DatesSetArg) => this.onDatesSet(info),
    height: "max(600px, calc(100vh - 600px))",
  };

  constructor(
    userService: UserService,
    public appService: AppService,
    public eventsService: EventsService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(userService);
  }

  ngOnInit() {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "events.events" }];
    const params = this.route.snapshot.queryParams;
    if (params["view"] === "calendar" || params["view"] === "list") {
      this.viewMode = params["view"];
    }
    if (params["month"]) {
      this.calendarOptions = { ...this.calendarOptions, initialDate: params["month"] + "-01" };
    }
    this.loadEvents();
  }

  loadEvents() {
    // Render cached results instantly for a snappy back-nav, but ALWAYS re-fetch underneath so a
    // newly-relevant event (e.g. one that just became ongoing) shows without a hard refresh — the
    // cache is a per-session snapshot and would otherwise go stale.
    if (this.eventsService.cachedPublicEvents.length) {
      this.applyEvents(this.eventsService.cachedPublicEvents, this.eventsService.cachedAllEvents);
      this.loading = false;
    } else {
      this.loading = true;
    }
    this.eventsService.getPublicEvents().subscribe({
      next: (result) => {
        this.applyEvents(result.events, result.allEvents);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private applyEvents(events: any[], allEvents: any[]) {
    this.events = events.map((e) => this.enrichEvent(e)).filter((e) => !this.endedBeforeToday(e));
    this.allEvents = allEvents.map((e) => this.enrichEvent(e)).filter((e) => !this.endedBeforeToday(e));
    this.groupByMonth();
    this.updateCalendarEvents();
  }

  private endedBeforeToday(event: any): boolean {
    if (!event.start_datetime) return false;
    const tz = event.timezone || "America/Chicago";
    const endDt = event.end_datetime || event.start_datetime;
    const endTime = this.parseDatetimeInTimezone(endDt, tz);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return endTime < startOfToday;
  }

  private groupByMonth() {
    const groups = new Map<string, any[]>();
    for (const event of this.events) {
      if (!event.start_datetime) continue;
      const [y, mo] = event.start_datetime.split("-");
      const key = `${y}-${mo}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(event);
    }
    this.groupedEvents = Array.from(groups.entries()).map(([key, events]) => {
      const [y, mo] = key.split("-");
      const label = new Date(+y, +mo - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
      return { label, events };
    });
  }

  private enrichEvent(event: any): any {
    if (event.start_datetime) {
      const date = this.parseDatetimeInTimezone(event.start_datetime, event.timezone || "America/Chicago");
      event._formattedDate = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
      event._startLocal = date.toISOString();
    }
    if (event.end_datetime) {
      const end = this.parseDatetimeInTimezone(event.end_datetime, event.timezone || "America/Chicago");
      event._endLocal = end.toISOString();
    }
    return event;
  }

  private parseDatetimeInTimezone(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }

  sortEvents() {
    if (this.appService.sortBy === "date") {
      this.events.sort((a, b) => {
        const cmp = (a.start_datetime || "").localeCompare(b.start_datetime || "");
        return this.appService.sortDirection === "asc" ? cmp : -cmp;
      });
    } else {
      this.events.sort((a, b) => {
        const cmp = (a.title || "").localeCompare(b.title || "");
        return this.appService.sortDirection === "asc" ? cmp : -cmp;
      });
    }
    this.groupByMonth();
  }

  switchView(mode: string) {
    this.viewMode = mode;
    const queryParams: any = { view: mode };
    if (mode === "calendar" && this.currentCalendarMonth) {
      queryParams.month = this.currentCalendarMonth;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: "merge", replaceUrl: true });
  }

  private currentCalendarMonth: string = "";

  onDatesSet(info: DatesSetArg) {
    const mid = new Date((info.start.getTime() + info.end.getTime()) / 2);
    this.currentCalendarMonth = `${mid.getFullYear()}-${String(mid.getMonth() + 1).padStart(2, "0")}`;
    if (this.viewMode === "calendar") {
      this.router.navigate([], { queryParams: { view: "calendar", month: this.currentCalendarMonth }, queryParamsHandling: "merge", replaceUrl: true });
    }
  }

  goToEvent(event: any) {
    this.router.navigate(["events", event.slug || event.id]);
  }

  private updateCalendarEvents() {
    this.calendarOptions = {
      ...this.calendarOptions,
      events: this.allEvents.map((e) => ({
        id: e.id,
        title: e.title,
        start: e._startLocal || e.start_datetime,
        end: e._endLocal || e.end_datetime,
        backgroundColor: "#c8102e",
        borderColor: "#c8102e",
        extendedProps: { event: e },
      })),
    };
  }

  onCalendarEventClick(info: EventClickArg) {
    const event = info.event.extendedProps.event;
    if (event) this.goToEvent(event);
  }
}
